import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/data";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteInfo } from "@/mocks/data";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
            <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">该文章可能已被删除或链接失效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    image: article.image,
    datePublished: article.date,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: siteInfo.name,
    },
  };

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <main className="min-h-screen bg-background-50">
        <article>
          <div className="pt-20">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: article.categoryName, href: `/category/${article.categoryId}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="max-w-4xl mx-auto px-4 md:px-6 pt-6 pb-10">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categoryId}`}
                className="bg-primary-100 text-primary-600 text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap cursor-pointer"
              >
                {article.categoryName}
              </Link>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>
            <h1
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {article.title}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
              {article.summary}
            </p>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          <div
            className="max-w-4xl mx-auto px-4 md:px-6 pb-16"
          >
            <div
              className="prose prose-sm md:prose-base max-w-none
                prose-headings:text-foreground-950 prose-headings:font-bold
                prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-ul:text-foreground-700 prose-li:mb-1
                prose-strong:text-foreground-950"
              style={{ fontFamily: "var(--font-body)" }}
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2
                className="text-xl md:text-2xl font-bold text-foreground-950 mb-8"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors mb-2">
                        {ra.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}